import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { useUserParams } from "../hooks/useUserParams";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });
  const { getCurrentAgentEmail } = useUserParams();
  const agentEmail = getCurrentAgentEmail();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create HTML message body with all form data
    const htmlMessage = `
      <h2 style="color: #2563eb; margin-bottom: 20px;">New Home Evaluation Request</h2>
      
      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Contact Information</h3>
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Phone:</strong> ${formData.phone}</p>
      </div>

      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Property Details</h3>
        <p><strong>Address:</strong> ${formData.address}</p>
        <p><strong>City:</strong> ${formData.city}</p>
        <p><strong>Province/State:</strong> ${formData.province}</p>
        <p><strong>Property Size:</strong> ${formData.propertySize}</p>
        <p><strong>Lot Size:</strong> ${formData.lotSize}</p>
        <p><strong>Property Type:</strong> ${formData.propertyType}</p>
        <p><strong>Building Type:</strong> ${formData.buildingType}</p>
      </div>

      ${formData.notes ? `
      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h3 style="color: #1e293b; margin-bottom: 15px;">Additional Notes</h3>
        <p style="white-space: pre-wrap;">${formData.notes}</p>
      </div>
      ` : ''}

      <div style="background-color: #dcfce7; padding: 15px; border-radius: 8px; border-left: 4px solid #16a34a;">
        <p style="margin: 0; color: #166534; font-weight: 600;">
          This evaluation request was submitted from the website contact form.
        </p>
      </div>
    `;

    const emailFormData = new FormData();
    emailFormData.append('data', JSON.stringify({
      recipientEmails: [agentEmail],
      senderEmail: process.env.REACT_APP_AGENT_EMAIL,
      subject: "New Home Evaluation Request",
      content: htmlMessage,
      senderName: formData.name,
      replyTo: formData.email,
    }));

    let EmailApiUrl = process.env.REACT_APP_NODE_ENV === "production" ? "https://3.138.143.209/global/send-email" : "http://3.138.143.209/global/send-email";

    try {
      const response = await fetch(EmailApiUrl, {
        method: "POST",
        body: emailFormData,
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      const res = await response.json();
      console.log("res", res);

      if (res?.message === "success") {
        console.log("Success", res);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          name: "",
          email: "",
          phone: "",
          address: "",
          city: "",
          province: "",
          propertySize: "",
          lotSize: "",
          propertyType: "",
          buildingType: "",
          notes: "",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  };

  const seoConfig = pageSEO.evaluation();
  return (
    <>
      <SEO {...seoConfig} />
      <div className="max-w-[1100px] pt-[150px] mx-auto grid md:grid-cols-2 px-6 gap-x-24 gap-y-10 min-h-[70vh] mb-12">
        <div>
          <h1 className="text-4xl libre">Free Home Evaluation</h1>

          <p className="font-semibold text-med-grey my-5">
            Wondering how much your home would sell for in today's market?
            Please complete the form below and I would be happy to send you a
            Free Home Evaluation.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-item">
              <label className="form-label">Name*</label>
              <input
                required
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Full Name"
              />
            </div>
            <div className="form-item">
              <label className="form-label">Email*</label>
              <input
                required
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Email Address"
              />
            </div>
            <div className="form-item">
              <label className="form-label">Phone*</label>
              <input
                required
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Phone Number"
              />
            </div>
            <div className="form-item">
              <label className="form-label">Property Address*</label>
              <input
                required
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Address of the Property"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-x-10">
              <div className="form-item">
                <label className="form-label">City*</label>
                <input
                  required
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="City of the Property"
                />
              </div>

              <div className="form-item">
                <label className="form-label">Province/State*</label>
                <input
                  required
                  name="provinceState"
                  value={formData.provinceState}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Province/State of the Property"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-x-10">
              <div className="form-item">
                <label className="form-label">Property Size*</label>
                <input
                  required
                  name="propertySize"
                  value={formData.propertySize}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Approx. Square Footage"
                />
              </div>
              <div className="form-item">
                <label className="form-label">Lot Size*</label>
                <input
                  required
                  name="lotSize"
                  value={formData.lotSize}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Approx. Square Footage/Acreage"
                />
              </div>
            </div>
            <div className="form-item">
              <label className="form-label">Property Type*</label>
              <select
                required
                name="propertyType"
                value={formData.propertyType}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Please Select</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Land">Land</option>
                <option value="Parking">Parking</option>
                <option value="Investment">Investment</option>
                <option value="Agricultural">Agricultural</option>
              </select>
            </div>
            <div className="form-item">
              <label className="form-label">Building Type*</label>
              <select
                required
                name="buildingType"
                value={formData.buildingType}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Please Select</option>
                <option value="House">House</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Condo">Condo</option>
                <option value="Duplex">Duplex</option>
                <option value="Triplex">Triplex</option>
                <option value="Office">Office</option>
                <option value="Mobile Home">Mobile Home</option>
                <option value="Retail">Retail</option>
              </select>
            </div>
            <div className="form-item">
              <label className="form-label">Other Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-input resize-none h-32"
                placeholder="Please tell me any other information you feel I should know about your property"
              />
            </div>
            <button
              type="submit"
              className="btn-secondary self-start rounded-none px-10 mt-3"
            >
              Submit
            </button>
          </form>
        </div>
        <div className="overflow-hidden h-[600px] ">
          <img className="h-full w-full object-cover" src={house} alt="House" />
        </div>
      </div>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-tertiary">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
